import { apiSlice } from './apiSlice'

/** Mirrors FastAPI `SaleClaimOut` (camelCase aliases) — frmCtrlSalesQryClaim datasheet. */
export interface SaleClaim {
  id: number
  claimScsCode: string | null
  claimId: string | null
  dateClaim: string | null
  customerInvoiceNo: string | null
  forDo: string | null
  contractRef: string | null
  client: string | null
  claimType: string | null
  claimCondition: string | null
  customerInvAtd: string | null
  dischargeDate: string | null
  weighingDate: string | null
  validWeighingDate: string | null
  validClaimDate: string | null
  acceptOrReject: string | null
  finalStatus: string | null
  paymentWeightTerms: string | null
  price: number | null
  grossShippedWeight: number | null
  netShippedWeight: number | null
  receiptWh: string | null
  grossLandedWeight: number | null
  netLandedWeight: number | null
  difference: number | null
  franchise: number | null
  claimWeight: number | null
  claimWeightValue: number | null
  claimReason: string | null
  claimQualityValue: number | null
  claimOtherValue: number | null
  creditNo: string | null
  fullCreditNo: string | null
  claimId2: string | null
  creditDate: string | null
  creditAmount: number | null
  customerDebitNo: string | null
  fromWarehouse: string | null
  debitDate: string | null
  debitNo: string | null
  fullDebitNo: string | null
  claimId3: string | null
  debitorTo: string | null
  debitCpCode: string | null
  debitName: string | null
  debitorRefNo: string | null
  debitReason: string | null
  debitValue: number | null
  note: string | null
  dateUpdated: string | null
  personUpdated: string | null
  address: string | null
  name: string | null
  paymentTerms: string | null
  warehouse: string | null
  blNumber: string | null
  destinationPort: string | null
  portOfLoading: string | null
  placeOfDelivery: string | null
  vesselNameVoyage: string | null
  goodsDescription: string | null
  deliveryTerms: string | null
  atd: string | null
  buyersRef: string | null
  customerInvPrice: string | null
  customerInvNw: number | null
  customerInvGw: number | null
  claimNote: string | null
  priceUnit: string | null
  roe: number | null
  claimValueFinal: number | null
  deliveryFrom: string | null
  isFinalized: number | null
  destinationPortName: string | null
  portOfLoadingName: string | null
}

export interface SaleClaimListResponse {
  items: SaleClaim[]
  total: number
  page: number
  pageSize: number
}

export interface SaleClaimListQuery {
  page: number
  pageSize: number
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

/** frmCtrlSalesQryClaimAdd New/Edit payload — camelCase → claim_scs. */
export interface SaleClaimWrite {
  claimScsCode?: string | null
  customerInvoiceNo?: string | null
  dateClaim?: string | null
  claimType?: string | null
  claimCondition?: string | null
  dischargeDate?: string | null
  weighingDate?: string | null
  finalStatus?: string | null
  priceUnit?: string | null
  claimReason?: string | null
  claimQualityValue?: number | null
  claimOtherValue?: number | null
  roe?: number | null
  note?: string | null
}

export type SaleClaimForm = Record<string, unknown> & { id: number }

export interface ClaimConditionSnapshot {
  weighingDate: number | null
  weightClaim: number | null
  qualityClaims: number | null
  others: number | null
}

export interface ClaimLookupItem { value: string; label: string; meta?: Record<string, unknown> | null }
export interface ClaimLookupResponse { items: ClaimLookupItem[]; total: number }
export interface SuggestClaimNoResponse { index: number; claimScsCode: string; exists: boolean }

const saleClaimApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleClaims: builder.query<SaleClaimListResponse, SaleClaimListQuery>({
      query: ({ page, pageSize, shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter }) => ({
        url: '/sale-claims',
        params: {
          page,
          pageSize,
          ...(shipmentPeriodFrom ? { shipmentPeriodFrom } : {}),
          ...(shipmentPeriodTo ? { shipmentPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleClaim'],
    }),

    getSaleClaim: builder.query<SaleClaim, number>({
      query: (id) => `/sale-claims/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleClaim', id }],
    }),

    deleteSaleClaim: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-claims/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleClaim'],
    }),

    getSaleClaimCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-claims/access/can-delete',
    }),

    // ── frmCtrlSalesQryClaimAdd ───────────────────────────────────────────
    getSaleClaimForm: builder.query<SaleClaimForm, number>({
      query: (id) => `/sale-claims/${id}/form`,
    }),
    createSaleClaim: builder.mutation<{ claim_scs_id: number }, SaleClaimWrite>({
      query: (body) => ({ url: '/sale-claims', method: 'POST', body }),
      invalidatesTags: ['SaleClaim'],
    }),
    updateSaleClaim: builder.mutation<{ claim_scs_id: number }, { id: number; body: SaleClaimWrite }>({
      query: ({ id, body }) => ({ url: `/sale-claims/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleClaim'],
    }),
    suggestClaimNo: builder.query<SuggestClaimNoResponse, void>({
      query: () => '/sale-claims/suggest/claim-no',
      keepUnusedDataFor: 0,
    }),
    checkClaimNo: builder.query<SuggestClaimNoResponse, { claimScsCode: string; excludeId?: number }>({
      query: ({ claimScsCode, excludeId }) => ({
        url: '/sale-claims/check/claim-no',
        params: { claimScsCode, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getClaimConditionSnapshot: builder.query<ClaimConditionSnapshot, string>({
      query: (organization) => ({ url: '/sale-claims/condition-snapshot', params: { organization } }),
      keepUnusedDataFor: 0,
    }),
    // Lookups
    claimLookupCustomerInvoices: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/customer-invoices', params: q ? { q } : {} }),
    }),
    claimLookupTypes: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/types', params: q ? { q } : {} }),
    }),
    claimLookupConditions: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/conditions', params: q ? { q } : {} }),
    }),
    claimLookupPriceUnits: builder.query<ClaimLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-claims/lookups/price-units', params: q ? { q } : {} }),
    }),
    // Print — chooser (claimable_at/notion) → rpCtrlSalesClaimCheck* (Weight vs Others/Quality).
    getClaimPrintPreview: builder.mutation<
      { title: string; html: string; message?: string },
      { claimId: number; claimableAt?: string; notion?: string }
    >({
      query: ({ claimId, claimableAt, notion }) => ({
        url: `/sale-claims/${claimId}/print/preview`,
        method: 'POST',
        body: { claimableAt, notion },
      }),
    }),
  }),
})

export const {
  useGetSaleClaimsQuery,
  useGetSaleClaimQuery,
  useDeleteSaleClaimMutation,
  useGetSaleClaimCanHardDeleteQuery,
  useLazyGetSaleClaimFormQuery,
  useCreateSaleClaimMutation,
  useUpdateSaleClaimMutation,
  useLazySuggestClaimNoQuery,
  useLazyCheckClaimNoQuery,
  useLazyGetClaimConditionSnapshotQuery,
  useClaimLookupCustomerInvoicesQuery,
  useClaimLookupTypesQuery,
  useClaimLookupConditionsQuery,
  useClaimLookupPriceUnitsQuery,
  useGetClaimPrintPreviewMutation,
} = saleClaimApi

export default saleClaimApi
